import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { ChevronLeft, ChevronRight, Droplets } from 'lucide-react'
import { navItems } from './navItems'

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, inboxBadge } = useUIStore()
  const location = useLocation()

  return (
    <aside
      className={cn(
        // C4: replaced deprecated bg-obsidian-900/70 (#05060B) with pure MIDNIGHT tile recipe
        // I5: narrowed from transition-all to transition-[width] to avoid repainting unrelated child props
        'relative z-20 hidden md:flex flex-col h-screen backdrop-blur-2xl hairline-r',
        'transition-[width] duration-300 ease-smooth shrink-0',
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
      style={{ background: 'var(--midnight-bg, #000000)' }}
    >
      {/* Logo */}
      <div className={cn(
        'flex items-center gap-3 px-4 py-6 hairline-b',
        sidebarCollapsed && 'justify-center px-0'
      )}>
        <div className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/[0.10] flex items-center justify-center shrink-0">
          <Droplets className="w-4 h-4 text-ink-100" />
        </div>
        {!sidebarCollapsed && (
          <div className="overflow-hidden">
            <span className="font-display text-2xl text-ink-50 leading-none block tracking-tightest">Flo<span className="italic-accent text-ink-300">·</span>Rite</span>
            <span className="eyebrow text-ink-400 mt-1 block">Business OS</span>
          </div>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path + '/'))
          const Icon = item.icon
          const badge = item.path === '/inbox' ? inboxBadge : (item.badge ?? 0)

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ease-smooth group relative',
                isActive
                  ? 'nav-pill-active'
                  : 'text-ink-300 hover:text-ink-50 hover:bg-white/[0.03]',
                sidebarCollapsed && 'justify-center px-0'
              )}
              title={sidebarCollapsed ? item.label : undefined}
            >
              {/* Active indicator bar */}
              {isActive && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-white/40 rounded-r" />
              )}

              <Icon className={cn('w-4 h-4 shrink-0 transition-colors', isActive ? 'text-ink-50' : 'text-ink-400 group-hover:text-ink-100')} />

              {!sidebarCollapsed && (
                <>
                  <span className="flex-1 truncate">{item.label}</span>
                  {badge > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold tabular-nums bg-peach-500/20 text-peach-200 border border-peach-500/30">
                      {badge}
                    </span>
                  )}
                </>
              )}

              {/* Collapsed badge dot */}
              {sidebarCollapsed && badge > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-peach-200 rounded-full shadow-[0_0_6px_rgba(255,162,105,0.6)]" />
              )}

              {/* Tooltip on collapsed */}
              {sidebarCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 glass rounded-md text-xs text-ink-100 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[21]">
                  {item.label}
                </div>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="p-2 hairline-t">
        <button
          onClick={toggleSidebar}
          className="w-full flex items-center justify-center gap-2 p-2 rounded-xl text-white/40 hover:text-ink-100 hover:bg-white/[0.04] transition-all duration-200"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed
            ? <ChevronRight className="w-4 h-4" />
            : <><ChevronLeft className="w-4 h-4" /><span className="text-xs">Collapse</span></>
          }
        </button>
      </div>
    </aside>
  )
}
